<template>
  <RevPrintWrapper class="ii-print">
    <div slot="header">
      <PrintHeader
        v-if="rev.name"
        logoUrlWidth="100"
        revFontSize="16"
        :revName="rev.name"
        :siteName="rev.siteName"
        :tags="rev.tags"
        :completedDate="rev.implementedDate"
        :creationDate="rev.creationDate"
        :logoMediaObject="logoMediaObject"
        :defaultLogo="DEFAULT_LOGO"
        :rev="rev"
        :statusText="rev.review.descriptionTitle"
        :validationDate="rev.validationDate"
        :isValidated="rev.isValidated"
        :isFinanciallyValidated="rev.isFinancialValidated"
        :isHighlighted="rev.isHighlighted || false"
        :challengeName="challengeName"
      />
    </div>
    <div slot="info">
      <v-row no-gutters class="ii-print-info">
        <v-col cols="7" class="flex-grow-1 flex-shrink-0">
          <CycleSteps
            :relateTitle="$t('common.rev_state.plan')"
            :relatePhoto="rev.relatePhoto"
            :problemQuestion="$t('rev_cycle.relate.what_is_the_problem')"
            :problemAnswer="rev.relateProblem"
            :ideaQuestion="$t('rev_cycle.relate.what_is_the_idea')"
            :ideaAnswer="rev.relateIdea"
            :expectedResultQuestion="
              $t('rev_cycle.relate.what_is_the_expected_result')
            "
            :expectedResultAnswer="rev.relateResult"
            :experimentTitle="$t('common.rev_state.do')"
            :experimentPhoto="rev.experimentPhoto"
            :experimentResultQuestion="
              $t('rev_cycle.experiment.what_did_you_do')
            "
            :experimentResultAnswer="rev.experimentDone"
            :expandTitle="$t('common.rev_state.act')"
            :expandPhoto="rev.expandPhoto"
            :expandQuestion="$t('rev_cycle.expand.what_is_the_solution')"
            :expandAnswer="rev.expandSustain"
          />
        </v-col>
        <v-col class="ii-print__column flex-grow-0 flex-shrink-1 ms-3" cols="5">
          <Team
            :title="$t('common.roles.collaborator_other')"
            :ownerText="$t('common.roles.owner')"
            :coachText="$t('common.roles.coach_one')"
            :membersText="$t('common.member_other')"
            :owner="rev.owner"
            :coach="rev.coach"
            :members="rev.members"
          />

          <div v-if="isTranslated" class="d-flex align-end justify-end">
            {{ $t('rev.content_translated') }}
          </div>
        </v-col>
      </v-row>

      <v-row no-gutters class="ii-print-info">
        <v-col cols="12" class="flex-grow-1 flex-shrink-0">
          <ImpactsPrintView :rev="rev" />
        </v-col>
      </v-row>
    </div>
  </RevPrintWrapper>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import _ from 'lodash'
import mixin from '@/mixins/mixin'
import { utils } from 'frontend-js-shared'
import userMixin from '@/mixins/userMixin'

import PrintHeader from '@/components/RevPrint/PrintHeader.vue'
import ReverLogoSrc from '@/assets/images/logo-rever.svg'
import Team from '@/containers/ImprovementIdeaPrintView/Team.vue'
import Check from '@/components/RevPrint/assets/check.svg'
import CycleSteps from './CycleSteps.vue'
import reviewExperimentFailed from '@/components/RevPrint/assets/reviewExperimentFailed.svg'
import reviewInProgress from '@/components/RevPrint/assets/reviewInProgress.svg'
import reviewRevImplemented from '@/components/RevPrint/assets/reviewRevImplemented.svg'
import RevPrintWrapper from '@/components/RevPrint/RevPrintWrapper.vue'
import currencies from '@/locales/currencies.json'
import ImpactsPrintView from '../../components/Rev/Review/ImpactsPrintView.vue'

export default {
  name: 'ImprovementIdeaPrintView',

  components: {
    PrintHeader,
    Team,
    CycleSteps,
    RevPrintWrapper,
    ImpactsPrintView,
  },

  mixins: [mixin, userMixin],

  props: {
    isTranslated: {
      required: false,
      type: Boolean,
    },
  },

  data() {
    return {
      DEFAULT_LOGO: ReverLogoSrc,
    }
  },

  computed: {
    ...mapState({
      accessToken: state => state.access.token,
      state: state => state,
      logoMediaObject: state => state.access.logoMediaObject,
      language: state => state.language ?? 'en',
    }),
    ...mapState('improvementIdeaPrint', {
      challenge: state => state.challenge,
      rev(state) {
        return {
          ...state.rev,
          name: state?.rev?.name,
          siteName: state?.rev?.site?.name,
          tags: state?.rev?.tags ?? [],
          isValidated: this.isValidated(state?.rev ?? false),
          isHighlighted: this.isHighlighted(state?.rev ?? false),
          isFinancialValidated: this.isFinancialValidated(state?.rev ?? false),
          validationDate: this.getValidationDate(state?.rev),
          costReducedPerMonth: {
            title: this.$t('common.total_cost_reduced'),
            amount: this.formatMoney(
              state?.rev?.financialMetadata?.currency ?? '',
              _.get(
                state,
                'rev.cycle.financialInformationCaptured.reviewCostReductionPerMonth.value',
                0
              )
            ),
            currency: state?.rev?.financialMetadata?.currency ?? '',
            monthText: this.$t('rev_cycle.review.per', { context: 'month' }),
          },
          costReducedPerYear: {
            amount: this.formatMoney(
              state?.rev?.financialMetadata?.currency ?? '',
              this.amountPerYear(
                _.get(state, 'rev.cycle.reviewCostReductionPerMonth', 0)
              )
            ),
            currency: state?.rev?.financialMetadata?.currency ?? '',
            yearText: this.$t('rev_cycle.review.per', { context: 'year' }),
          },
          revenueGeneratedPerMonth: {
            title: this.$t('common.total_revenue_generated'),
            amount: this.formatMoney(
              state?.rev?.financialMetadata?.currency ?? '',
              _.get(
                state,
                'rev.cycle.financialInformationCaptured.reviewNewRevenuePerMonth.value',
                0
              )
            ),
            currency: state?.rev?.financialMetadata?.currency ?? '',
            monthText: this.$t('rev_cycle.review.per', { context: 'month' }),
          },
          revenueGeneratedPerYear: {
            amount: this.formatMoney(
              state?.rev?.financialMetadata?.currency ?? '',
              this.amountPerYear(
                _.get(state, 'rev.cycle.reviewNewRevenuePerMonth', 0)
              )
            ),
            currency: state?.rev?.financialMetadata?.currency ?? '',
            yearText: this.$t('rev_cycle.review.per', { context: 'year' }),
          },
          investment: {
            title: this.$t('common.total_investment'),
            amount: this.formatMoney(
              state?.rev?.financialMetadata?.currency ?? '',
              _.get(
                state,
                'rev.cycle.financialInformationCaptured.reviewTotalInvestmentMade.value',
                0
              )
            ),
            currency: state?.rev?.financialMetadata?.currency ?? '',
          },
          owner: {
            name: this.getFirstWord(this.getFirstName(state.rev.owner)),
            lastName: this.getFirstWord(this.getLastName(state.rev.owner)),
            avatarMediaObject: state.rev.owner?.photo,
            email: _.get(state, 'rev.owner.email'),
          },
          coach: {
            name: this.getFirstWord(this.getFirstName(state.rev.coach)),
            lastName: this.getFirstWord(this.getLastName(state.rev.coach)),
            avatarMediaObject: state.rev.coach?.photo,
            email: _.get(state, 'rev.coach.email'),
          },
          members: _.get(state, 'rev.collaborators', []).map(collaborator => ({
            avatarMediaObject: collaborator?.photo,
            name: this.getFirstWord(this.getFirstName(collaborator)),
            lastName: this.getFirstWord(this.getLastName(collaborator)),
            email: _.get(collaborator, 'email'),
          })),
          categories: this.getCategories(_.get(state, 'rev.categories')),
          relatePhoto: _.get(state, 'rev.cycle.relatePhoto'),
          relateProblem: _.get(state, 'rev.cycle.relateProblem', ''),
          relateIdea: _.get(state, 'rev.cycle.relateIdea', ''),
          relateResult: _.get(state, 'rev.cycle.relateResult', ''),
          experimentPhoto: _.get(state, 'rev.cycle.experimentPhoto'),
          experimentDone: _.get(state, 'rev.cycle.experimentDone', ''),
          expandPhoto: _.get(state, 'rev.cycle.expandPhoto'),
          expandSustain: _.get(state, 'rev.cycle.expandSustain', ''),
          revState: _.get(state, 'rev.revState'),
          creationDate: this.getCreationDate(state?.rev),
          review: {
            title: this.$t('revPrint.review_title'),
            ...this.getReviewData(state.rev),
          },
          implementedDate: this.getImplementedDate(state),
          comments: _.get(state, 'rev.cycle.reviewComments'),
          timeInvested: parseInt(state?.rev?.cycle?.reviewTimeInvested) || null,
          timeReductionByMonth:
            parseInt(state?.rev?.cycle?.reviewTimeReductionByMonth) || null,
        }
      },
    }),

    challengeName() {
      return this.challenge?.name ?? ''
    },
  },

  methods: {
    amountPerYear(amount) {
      return amount * 12
    },
    ...mapActions({
      getRev: 'improvementIdeaPrint/getRev',
      getRevChallenge: 'improvementIdeaPrint/getRevChallenge',
      translate: 'improvementIdeaPrint/translateRev',
    }),
    isValidated(rev) {
      return (
        rev.validations__implementation__hasAnswer &&
        rev.validations__implementation__isValidated
      )
    },
    isHighlighted(rev) {
      return (
        rev.validations__implementation__hasAnswer &&
        rev.highlight__isHighlighted
      )
    },
    isFinancialValidated(rev) {
      return (
        rev.validations__financial__hasAnswer &&
        rev.validations__financial__isValidated
      )
    },
    getOrganizationId() {
      return this.getRouteParams().organizationId
    },
    getRevId() {
      return this.getRouteParams().revId
    },
    getRouteParams() {
      return this.$route.params
    },

    getValidationDate(rev) {
      const rawDate = rev?.validations__implementation__validatedAt
      if (!rawDate) return ''
      return utils.dates.formatDate(rawDate)
    },

    getCreationDate(rev) {
      const rawDate = rev?.createdAt
      if (!rawDate) return ''
      return utils.dates.formatDate(rawDate)
    },

    getImplementedDate(state) {
      let implementedDate = _.get(state, 'rev.implementedAt')
      if (implementedDate) {
        implementedDate = utils.dates.formatDate(implementedDate)
      }
      return implementedDate
    },

    formatMoney(currency, rawAmount) {
      const amount = Math.round(rawAmount)
      const currencySymbol = currencies[currency]?.symbol_native ?? ''

      return `${currencySymbol}${amount.toLocaleString('en')}`
    },
    getReviewData(rev) {
      if (this.isValidated(rev)) {
        return {
          iconPath: reviewRevImplemented,
          descriptionTitle: this.$t('common.rev_condition.validated'),
        }
      }

      switch (rev.revCondition) {
        case 'failed':
        case 'learned':
        case 'notImplemented':
        case 'canceled':
          return {
            iconPath: reviewExperimentFailed,
            descriptionTitle: this.$t('common.rev_condition.canceled'),
          }
        case 'open':
          return {
            iconPath: reviewRevImplemented,
            descriptionTitle: this.$t('common.rev_condition.open'),
          }
        case 'onHold':
          return {
            iconPath: reviewRevImplemented,
            descriptionTitle: this.$t('common.rev_condition.on_hold'),
          }
        case 'inReview':
          return {
            iconPath: reviewRevImplemented,
            descriptionTitle: this.$t('common.rev_condition.in_review'),
          }
        case 'implemented':
          return {
            iconPath: reviewRevImplemented,
            descriptionTitle: this.$t('common.rev_condition.completed'),
          }
        default:
          return {
            iconPath: reviewInProgress,
            descriptionTitle: this.$t('common.rev_condition.in_progress'),
          }
      }
    },

    getCategories(categories) {
      if (!categories) return []

      return categories.map(category => ({
        iconPath: Check,
        text: this.$t(`common.categories.${category}`),
      }))
    },

    async translateRev() {
      const fieldsToTranslate = [
        'name',
        'cycle.relateProblem',
        'cycle.relateIdea',
        'cycle.relateResult',
        'cycle.expandLearn',
        'cycle.expandSustain',
        'cycle.experimentDone',
        'cycle.reviewComments',
        'cycle.reviewNotImplementedExplanation',
      ]

      await this.translate({
        revId: this.getRevId(),
        organizationId: this.getOrganizationId(),
        targetLanguage: this.$store.getters.language,
        fieldsToTranslate,
      })
    },
  },
  mounted() {
    if (this.isTranslated) {
      this.translateRev()
    } else {
      this.getRev({
        organizationId: this.getOrganizationId(),
        revId: this.getRevId(),
      })
    }
    this.getRevChallenge({
      organizationId: this.getOrganizationId(),
      revId: this.getRevId(),
    })
  },
}
</script>

<style lang="scss" scoped>
@media screen, print {
  .ii-print {
    width: 95% !important;
    margin-left: 25px !important;
    &__review {
      max-height: 30px;
    }
  }

  .ii-print-info {
    flex-wrap: nowrap;
  }
}
</style>
